import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import { Box, Button, Chip, IconButton, } from '@mui/material';
import { ChevronRight, } from '@mui/icons-material';
import { observer } from 'mobx-react-lite';
import { useConversationStore } from '../ConversationStoreProvider';
import ConnectedConversation from './ConnectedConversation';
import ProductDetailPanel from './product/ProductDetailPanel';
import Conversation from './Conversation';
import DualSearchTopSection from './DualSearchTopSection';
import useDualModeResize from './hooks/useDualModeResize';
import { IsMobileViewProvider } from './IsMobileViewContext';
import { useTokens } from '../tokens/TokensProvider';
import { DEFAULT_DRAWER_WIDTH, DEFAULT_INPUT_HEIGHT, DEFAULT_MIN_EXPANDED_HEIGHT, AI_SEARCH_TOGGLE_COLLAPSED_HEIGHT, } from './constants/dualModeConstants';
// Product detail panel specifically for AI Search - now using separate component
const ProductDetailPanelWrapper = ({ product }) => _jsx(ProductDetailPanel, { product: product });
const Search = ({ searchQuery, adminOptions, agentAvatarImage, agentName, animatedProcessingImage, bodyStyle, courseCardShowDescLines, courseCardShowImage, courseCardShowInstructor, courseCardShowTitle, enableMultiRefinements, enableVoiceInput, feedbackButton, hideRightRailOnMobile, inputStyle, maxItemsInRightRail, multiRefinementSubmitButtonText, openWebPagesInNewTab, questionAlignment, questionStyle, referenceFormatting, resetConversationLabel, responseAlignment, responseStyle, sendImage, showItemReferencesWithMessage = true, showResetWithInput, startingMessageFontStyle, userAvatarImage, productFormatting, 
// Semantic props from SearchWidget (NO generic flags)
hideFirstMessage = false, disableAutoScroll = false, layoutMode = 'ai', messageStackMode, topSectionConfig, allowCollapseSearchWidget = false, disableInternalScroll: disableInternalScrollProp, showPersistentPrompts, stripTrailingAsterisk, disableConversationPersistence, 
// Layout config props
initialHeight, 
// inputHeight, paddingBottom, minExpandedHeight are hardcoded - not configurable
headerHeightValue, headerCssVariable, useCssVariable, 
// Legacy props (for backward compatibility, will be removed)
disableArrowScroll, disableAddSpaceAnimation, itemReferenceDisplay, }) => {
    const store = useConversationStore();
    const tokens = useTokens();
    const aiSearchToggleOffText = tokens.getValue('aiSearchToggleOffText');
    const aiSearchToggleOnText = tokens.getValue('aiSearchToggleOnText');
    const seeMoreText = tokens.getValue('seeMoreText');
    const seeLessText = tokens.getValue('seeLessText');
    const resolvedSearchQuery = React.useMemo(() => {
        if (!stripTrailingAsterisk || !searchQuery) {
            return searchQuery;
        }
        const trimmed = searchQuery.trim();
        if (!trimmed.endsWith('*')) {
            return searchQuery;
        }
        const cleaned = trimmed.replace(/\*+$/, '').trim();
        return cleaned || '';
    }, [searchQuery, stripTrailingAsterisk]);
    // Use semantic props directly - NO conditional logic based on generic flags
    const isDual = layoutMode === 'dual';
    const collapseAllowed = allowCollapseSearchWidget ?? false;
    const [aiSearchEnabled, setAiSearchEnabled] = useState(true);
    const dualLayoutEnabled = isDual;
    const dualModeActive = isDual && aiSearchEnabled;
    /** Legacy: when unset, dual mode hid persistent prompts; AI mode showed them. Explicit true/false always wins. */
    const resolvedShowPersistentPrompts = showPersistentPrompts === false
        ? false
        : showPersistentPrompts === true
            ? true
            : !isDual;
    // Get header height from props (resolved in SearchWidget) or fallback
    const finalHeaderHeightValue = headerHeightValue || '88px';
    const finalHeaderCssVariable = headerCssVariable || '--header-height';
    const finalUseCssVariable = useCssVariable ?? true;
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [renderCollapsed, setRenderCollapsed] = useState(collapseAllowed);
    const [externalVisibleTop, setExternalVisibleTop] = useState(null);
    const [externalVisibleHeight, setExternalVisibleHeight] = useState(null);
    const [externalViewportHeight, setExternalViewportHeight] = useState(null);
    const [externalIframeHeight, setExternalIframeHeight] = useState(null);
    const [seeMoreButtonHeight, setSeeMoreButtonHeight] = React.useState(undefined);
    const [measuredInputHeight, setMeasuredInputHeight] = React.useState(null);
    const [measuredLeftColumnHeight, setMeasuredLeftColumnHeight] = React.useState(null);
    const mainContainerRef = React.useRef(null);
    const previousHeightRef = React.useRef(null);
    const toggleButtonRef = React.useRef(null);
    React.useEffect(() => {
        if (!collapseAllowed) {
            setRenderCollapsed(false);
        }
    }, [collapseAllowed]);
    const handleProductClick = React.useCallback((product) => {
        setSelectedProduct(product);
    }, []);
    const handleCloseClick = React.useCallback(() => {
        setSelectedProduct(null);
    }, []);
    // Resolve tokens for topSection labels
    const resolvedTopSectionConfig = React.useMemo(() => {
        if (!topSectionConfig)
            return undefined;
        return {
            ...topSectionConfig,
            seeMoreLabel: seeMoreText,
            seeLessLabel: seeLessText,
        };
    }, [
        topSectionConfig,
        seeMoreText,
        seeLessText,
    ]);
    // Drawer logic for dual mode (calculated after selectedProduct is defined)
    const isDrawerOpen = isDual && selectedProduct !== null;
    const finalDrawerWidth = DEFAULT_DRAWER_WIDTH;
    // Refs for dual mode resize hook
    const conversationContainerRef = React.useRef(null);
    const leftColumnRef = React.useRef(null);
    const contentRef = React.useRef(null);
    const messagesContainerRef = React.useRef(null);
    const streamingMessagesScrollRef = React.useRef(null);
    const seeMoreButtonRef = React.useRef(null);
    const drawerWrapperRef = React.useRef(null);
    const resolvedInitialHeight = initialHeight ?? 500;
    const toggleInsetPx = collapseAllowed ? 8 : 0;
    const toggleReserveHeightPx = collapseAllowed ? 16 : 0;
    const resolvedInputHeight = DEFAULT_INPUT_HEIGHT;
    const resolvedMinExpandedHeight = DEFAULT_MIN_EXPANDED_HEIGHT;
    const collapsedContentMaxHeight = isDual
        ? Math.max(0, resolvedInitialHeight
            - (measuredInputHeight ?? resolvedInputHeight)
            - (seeMoreButtonHeight ?? 0)
            - toggleReserveHeightPx)
        : null;
    const collapsedMaxHeightValue = (isDual && renderCollapsed && collapsedContentMaxHeight !== null)
        ? `${collapsedContentMaxHeight}px`
        : 'none';
    // Use dual mode resize hook only in dual mode
    const { contentExceedsThreshold, handleToggleCollapse: handleToggleCollapseFromHook, } = useDualModeResize({
        dualSearchEnabled: dualLayoutEnabled,
        renderCollapsed,
        isStreamingActive: false,
        initialHeight: resolvedInitialHeight,
        finalInputHeight: resolvedInputHeight,
        finalMinExpandedHeight: resolvedMinExpandedHeight,
        measuredInputHeight,
        seeMoreButtonHeight,
        conversationContainerRef,
        leftColumnRef,
        contentRef,
        messagesContainerRef,
        streamingMessagesScrollRef,
        conversation: store.currentConversation,
        onToggleCollapse: (newCollapsedState) => {
            setRenderCollapsed(newCollapsedState);
        },
    });
    const handleToggleCollapse = () => {
        if (!collapseAllowed) {
            return;
        }
        handleToggleCollapseFromHook();
    };
    const handleToggleAiSearch = () => {
        const prevEnabled = aiSearchEnabled;
        const nextEnabled = !prevEnabled;
        if (nextEnabled === false && mainContainerRef.current) {
            const currentHeight = mainContainerRef.current.offsetHeight;
            previousHeightRef.current = currentHeight;
        }
        setAiSearchEnabled(nextEnabled);
        setTimeout(() => {
            if (typeof window !== 'undefined' && window.parent && window.parent !== window) {
                if (nextEnabled === false) {
                    const collapseHeight = toggleButtonRef.current?.offsetHeight
                        ?? AI_SEARCH_TOGGLE_COLLAPSED_HEIGHT;
                    const message = {
                        source: 'microcommerce-ai-embed',
                        type: 'expandIframe',
                        mode: 'collapsed',
                        height: collapseHeight + 5,
                    };
                    window.parent.postMessage(message, '*');
                }
                else {
                    const heightToRestore = previousHeightRef.current || resolvedInitialHeight || 500;
                    const message = {
                        source: 'microcommerce-ai-embed',
                        type: 'expandIframe',
                        mode: 'expanded',
                        height: heightToRestore,
                    };
                    window.parent.postMessage(message, '*');
                }
            }
            else {
                console.warn('[AISearchToggle] Cannot send - window.parent check failed', {
                    hasWindow: typeof window !== 'undefined',
                    hasParent: !!window?.parent,
                    isSameWindow: window?.parent === window,
                });
            }
        }, 0);
    };
    // Calculate disableInternalScroll based on prop override + dual defaults
    let disableInternalScroll;
    if (isDual) {
        disableInternalScroll = typeof disableInternalScrollProp === 'boolean'
            ? disableInternalScrollProp
            : renderCollapsed;
    }
    else {
        disableInternalScroll = disableInternalScrollProp ?? false;
    }
    // Calculate topSpaceReserved from header config
    const topSpaceReserved = finalUseCssVariable
        ? `var(${finalHeaderCssVariable}, ${finalHeaderHeightValue})`
        : finalHeaderHeightValue;
    // Listen for host visibility updates only in dual mode and when expanded
    React.useEffect(() => {
        if (!aiSearchEnabled || !isDual || renderCollapsed) {
            setExternalVisibleTop(null);
            setExternalVisibleHeight(null);
            setExternalViewportHeight(null);
            setExternalIframeHeight(null);
            return undefined;
        }
        const handleMessage = (event) => {
            if (event.data?.source !== 'microcommerce-ai-embed-parent') {
                return;
            }
            if (event.data.type === 'dual:host-visibility') {
                const { visibleTop, visibleHeight, viewportHeight, iframeHeight, containerHeight, } = event.data;
                setExternalVisibleTop(typeof visibleTop === 'number' ? visibleTop : null);
                setExternalVisibleHeight(typeof visibleHeight === 'number' ? visibleHeight : null);
                setExternalViewportHeight(typeof viewportHeight === 'number' ? viewportHeight : null);
                // Prefer iframeHeight, fallback to containerHeight if provided
                if (typeof iframeHeight === 'number') {
                    setExternalIframeHeight(iframeHeight);
                }
                else if (typeof containerHeight === 'number') {
                    setExternalIframeHeight(containerHeight);
                }
            }
            if (event.data.type === 'dual:container-height') {
                const { height } = event.data;
                if (typeof height === 'number') {
                    setExternalIframeHeight(height);
                }
            }
        };
        window.addEventListener('message', handleMessage);
        // Request visibility tracking from host when expanded
        if (window.parent && window.parent !== window) {
            window.parent.postMessage({
                source: 'microcommerce-ai-embed',
                type: 'dual:request-visibility',
            }, '*');
        }
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [aiSearchEnabled, isDual, renderCollapsed]);
    /* AI Search Layout - 2 columns (dual mode) or single column (ai mode) */
    const externalPositioningActive = dualModeActive && !renderCollapsed && selectedProduct;
    // Measure See More button container height for collapsedMaxHeight calculation in Conversation
    // Need to measure the Box container, not just the Button, as the Box has its own height
    const seeMoreButtonContainerRef = React.useRef(null);
    // Only show button if content actually exceeds the collapsed threshold
    const seeMoreButtonVisible = dualLayoutEnabled && contentExceedsThreshold;
    const seeMoreButtonPadding = seeMoreButtonVisible && !renderCollapsed
        ? (seeMoreButtonHeight ?? 64)
        : 0;
    React.useLayoutEffect(() => {
        // Only measure if button is visible (content exceeds threshold)
        const buttonVisible = isDual && contentExceedsThreshold;
        if (!buttonVisible || !seeMoreButtonContainerRef.current) {
            setSeeMoreButtonHeight(undefined);
            return undefined;
        }
        let resizeObserver = null;
        const measureContainer = () => {
            const container = seeMoreButtonContainerRef.current;
            if (container && container.isConnected) {
                const height = Math.max(container.offsetHeight || 0, container.clientHeight || 0, container.scrollHeight || 0);
                if (height > 0) {
                    setSeeMoreButtonHeight((prev) => {
                        if (prev !== height) {
                            return height;
                        }
                        return prev;
                    });
                }
            }
        };
        // Use requestAnimationFrame to ensure DOM is ready
        const rafId = requestAnimationFrame(() => {
            measureContainer();
            resizeObserver = new ResizeObserver(measureContainer);
            if (seeMoreButtonContainerRef.current) {
                resizeObserver.observe(seeMoreButtonContainerRef.current);
            }
        });
        return () => {
            cancelAnimationFrame(rafId);
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
        };
    }, [isDual, contentExceedsThreshold]);
    // Measure left column height to limit right panel max-height
    React.useLayoutEffect(() => {
        // Only measure in dual mode when expanded and product is selected
        if (!isDual || renderCollapsed || !leftColumnRef.current) {
            setMeasuredLeftColumnHeight(null);
            return undefined;
        }
        let resizeObserver = null;
        const measureLeftColumn = () => {
            const column = leftColumnRef.current;
            if (column && column.isConnected) {
                const height = Math.max(column.offsetHeight || 0, column.clientHeight || 0, column.scrollHeight || 0);
                if (height > 0) {
                    setMeasuredLeftColumnHeight((prev) => {
                        if (prev !== height) {
                            return height;
                        }
                        return prev;
                    });
                }
            }
        };
        const rafId = requestAnimationFrame(() => {
            measureLeftColumn();
            resizeObserver = new ResizeObserver(measureLeftColumn);
            if (leftColumnRef.current) {
                resizeObserver.observe(leftColumnRef.current);
            }
        });
        return () => {
            cancelAnimationFrame(rafId);
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
        };
    }, [isDual, renderCollapsed]);
    const viewportHeightFallback = typeof window !== 'undefined'
        ? (window.innerHeight || document.documentElement?.clientHeight || 0)
        : 0;
    const hostViewportHeight = externalViewportHeight ?? viewportHeightFallback;
    const iframeHeight = externalIframeHeight ?? hostViewportHeight;
    const baseVisibleHeight = externalVisibleHeight ?? hostViewportHeight;
    const heightCandidates = [baseVisibleHeight];
    if (hostViewportHeight > 0) {
        heightCandidates.push(hostViewportHeight);
    }
    if (iframeHeight > 0) {
        heightCandidates.push(iframeHeight);
    }
    const effectiveMaxHeight = externalPositioningActive && heightCandidates.length > 0
        ? Math.max(320, Math.min(...heightCandidates))
        : null;
    const visibleHeightForStyles = effectiveMaxHeight ?? 0;
    const safeVisibleTop = externalPositioningActive
        ? Math.max(externalVisibleTop || 0, 0)
        : 0;
    const measuredDrawerHeight = drawerWrapperRef.current
        ? Math.max(drawerWrapperRef.current.scrollHeight || 0, drawerWrapperRef.current.offsetHeight || 0, drawerWrapperRef.current.clientHeight || 0)
        : null;
    const drawerHeightForClamp = (() => {
        const fallbackVisible = externalVisibleHeight ?? visibleHeightForStyles ?? 0;
        const usableMeasured = measuredDrawerHeight && measuredDrawerHeight > 0
            ? measuredDrawerHeight
            : null;
        const heightCandidate = usableMeasured
            ? Math.max(usableMeasured, fallbackVisible)
            : fallbackVisible;
        if (!externalPositioningActive) {
            return heightCandidate || 0;
        }
        const iframeBound = externalIframeHeight ?? Number.POSITIVE_INFINITY;
        // Use left column height as upper bound when available
        const leftColumnBound = measuredLeftColumnHeight ?? Number.POSITIVE_INFINITY;
        return Math.max(0, Math.min(heightCandidate || 0, iframeBound, leftColumnBound));
    })();
    const effectiveDrawerHeight = drawerHeightForClamp > 0
        ? drawerHeightForClamp
        : Math.max(externalVisibleHeight || 0, visibleHeightForStyles || 0, 1);
    const maxDrawerTop = externalPositioningActive && externalIframeHeight !== null
        ? Math.max(0, (externalIframeHeight ?? 0) - effectiveDrawerHeight)
        : null;
    const [lockedDrawerTop, setLockedDrawerTop] = React.useState(0);
    React.useEffect(() => {
        if (!externalPositioningActive) {
            setLockedDrawerTop(0);
            return;
        }
        if (maxDrawerTop === null) {
            setLockedDrawerTop(safeVisibleTop);
            return;
        }
        setLockedDrawerTop((prevTop) => {
            const clampedTarget = Math.min(safeVisibleTop, maxDrawerTop);
            const prevLockedAtBottom = prevTop >= maxDrawerTop && safeVisibleTop >= maxDrawerTop;
            if (prevLockedAtBottom) {
                return maxDrawerTop;
            }
            return clampedTarget;
        });
    }, [externalPositioningActive, maxDrawerTop, safeVisibleTop]);
    const drawerTop = externalPositioningActive ? lockedDrawerTop : safeVisibleTop;
    let resolvedMessageStackMode = messageStackMode;
    if (layoutMode === 'dual') {
        resolvedMessageStackMode = disableInternalScroll ? 'natural' : 'fill';
    }
    else if (!resolvedMessageStackMode) {
        resolvedMessageStackMode = 'fill';
    }
    const expandedMinHeight = (!renderCollapsed && isDual && externalIframeHeight)
        ? Math.max(resolvedInitialHeight, externalIframeHeight)
        : resolvedInitialHeight;
    const containerStyle = (() => {
        const base = {
            height: 'auto',
            minHeight: resolvedMessageStackMode === 'natural' ? 'auto' : '100%',
            width: '100%',
            margin: 0,
            padding: 0,
        };
        if (isDual) {
            // Dual mode: flex row with proper height constraints
            // - Collapsed: fixed height = initialHeight
            // - Expanded: auto height BUT minHeight = initialHeight (to prevent right panel overlap)
            return {
                ...base,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'stretch', // Both columns same height
                overflow: externalPositioningActive ? 'visible' : 'hidden',
                // Collapsed: fixed height; Expanded: auto BUT always minHeight = initialHeight
                height: renderCollapsed ? `${resolvedInitialHeight}px` : 'auto',
                minHeight: `${expandedMinHeight}px`, // Expanded: follow iframe height when available
            };
        }
        return {
            ...base,
            display: 'grid',
            gridTemplateColumns: '1fr 40%',
            overflow: 'hidden',
        };
    })();
    const rightPanelStyle = (() => {
        const base = {
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#f9fafb',
            borderLeft: '1px solid #e5e7eb',
            width: isDrawerOpen ? `${finalDrawerWidth}px` : '0px',
            transition: 'width 0.3s ease-in-out',
            flexShrink: 0,
        };
        if (externalPositioningActive) {
            return {
                ...base,
                overflow: 'visible',
                position: 'relative',
                alignSelf: 'flex-start',
            };
        }
        // Non-external positioning: height follows container (via alignItems: stretch)
        // - Collapsed: container has fixed height, panel follows
        // - Expanded: container grows with content, panel follows (capped at 100vh for scroll)
        return {
            ...base,
            overflow: 'hidden',
            height: '100%', // Follow container height (not fixed 100vh)
            maxHeight: renderCollapsed ? `${resolvedInitialHeight}px` : '100vh',
            position: 'sticky',
            top: 0,
            alignSelf: 'stretch', // Stretch to match left column height
        };
    })();
    const selectedProductWrapperStyle = (() => {
        const base = {
            display: 'flex',
            flexDirection: 'column',
            width: finalDrawerWidth,
        };
        if (externalPositioningActive) {
            return {
                ...base,
                position: 'fixed',
                top: `${drawerTop}px`,
                right: 0,
                maxHeight: `${drawerHeightForClamp}px`,
                height: `${drawerHeightForClamp}px`,
                zIndex: 5,
            };
        }
        // Height follows parent (rightPanelStyle) which follows container
        // - Collapsed: fixed to initialHeight
        // - Expanded: follows content height (capped at 100vh)
        return {
            ...base,
            height: '100%', // Follow parent height
            maxHeight: renderCollapsed ? `${resolvedInitialHeight}px` : '100vh',
        };
    })();
    const bodyMaxHeight = externalPositioningActive
        ? Math.max(0, drawerHeightForClamp - parseFloat(finalHeaderHeightValue || '0') - 1)
        : undefined;
    const productPanelBodyStyle = externalPositioningActive ? {
        flex: 1,
        minHeight: 0,
        maxHeight: `${bodyMaxHeight}px`,
        overflow: 'auto',
    } : {
        flex: 1,
        minHeight: 0,
        maxHeight: 'calc(95vh - var(--header-height, 3.5rem) - 1px)',
        overflow: 'auto',
    };
    const contentWrapperStyle = aiSearchEnabled
        ? {
            ...containerStyle,
            paddingTop: toggleReserveHeightPx,
        }
        : {
            ...containerStyle,
            height: 0,
            minHeight: 0,
            maxHeight: 0,
            overflow: 'hidden',
            pointerEvents: 'none',
            visibility: 'hidden',
        };
    // Main container style: fixed height when collapsed, auto when expanded
    const mainContainerStyle = {
        position: 'relative',
        width: '100%',
        // Collapsed: fixed height to anchor See More button at bottom
        // Expanded: auto height BUT minHeight = initialHeight (to prevent right panel overlap)
        ...(isDual ? {
            ...(renderCollapsed ? {
                height: `${resolvedInitialHeight}px`,
                minHeight: `${resolvedInitialHeight}px`,
                maxHeight: `${resolvedInitialHeight}px`,
            } : {
                minHeight: `${expandedMinHeight}px`, // Expanded: follow iframe height when available
            }),
        } : {}),
    };
    return (_jsx(IsMobileViewProvider, { children: _jsxs("div", { ref: mainContainerRef, className: "microcommerce-search-div", style: mainContainerStyle, children: [collapseAllowed && (_jsx(Box, { sx: {
                        position: 'absolute',
                        top: toggleInsetPx,
                        right: toggleInsetPx,
                        zIndex: 20,
                        display: 'flex',
                        gap: 1,
                    }, children: _jsx(Chip, { ref: toggleButtonRef, variant: "outlined", size: "small", onClick: handleToggleAiSearch, className: "site-system-chip-secondary", sx: {
                            textTransform: 'none',
                            whiteSpace: 'nowrap',
                            borderStyle: 'none',
                            paddingX: 2,
                            paddingY: 0,
                        }, label: aiSearchEnabled ? aiSearchToggleOffText : aiSearchToggleOnText }) })), _jsxs("div", { style: contentWrapperStyle, children: [_jsx("div", { ref: leftColumnRef, style: {
                                display: 'flex',
                                flexDirection: 'column',
                                borderRight: isDual ? 'none' : '1px solid #eee',
                                overflow: 'hidden',
                                flex: isDual ? 1 : undefined,
                            }, children: _jsxs("div", { ref: contentRef, style: {
                                    flex: 1,
                                    minHeight: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    paddingBottom: seeMoreButtonPadding ? `${seeMoreButtonPadding}px` : 0,
                                }, children: [isDual && resolvedTopSectionConfig && (_jsx(DualSearchTopSection, { topSectionMaxHeightPx: renderCollapsed ? resolvedTopSectionConfig.maxHeight : undefined, seeMoreLabel: resolvedTopSectionConfig.seeMoreLabel, seeLessLabel: resolvedTopSectionConfig.seeLessLabel, referenceFormatting: referenceFormatting, openWebPagesInNewTab: openWebPagesInNewTab })), _jsx(ConnectedConversation, { initialMessage: resolvedSearchQuery, agentName: agentName, limitToPageWidth: false, showItemReferencesWithMessage: isDual ? false : showItemReferencesWithMessage, showItemReferencesCarousel: false, showResetWithInput: showResetWithInput, courseCardShowImage: courseCardShowImage, courseCardShowTitle: courseCardShowTitle, courseCardShowInstructor: courseCardShowInstructor, courseCardShowDescLines: courseCardShowDescLines, openWebPagesInNewTab: openWebPagesInNewTab, style: bodyStyle, questionStyle: questionStyle, responseStyle: responseStyle, feedbackButton: feedbackButton, questionAlignment: questionAlignment, responseAlignment: responseAlignment, referenceFormatting: referenceFormatting, inputStyle: inputStyle, startingMessageFontStyle: startingMessageFontStyle, adminOptions: adminOptions, sendImage: sendImage, enableVoiceInput: enableVoiceInput, agentAvatarImage: agentAvatarImage, userAvatarImage: userAvatarImage, resetConversationLabel: resetConversationLabel, hideRightRailOnMobile: hideRightRailOnMobile, enableMultiRefinements: enableMultiRefinements, multiRefinementSubmitButtonText: multiRefinementSubmitButtonText, maxItemsInRightRail: maxItemsInRightRail, animatedProcessingImage: animatedProcessingImage, productFormatting: productFormatting, onProductClick: handleProductClick, itemReferenceDisplay: itemReferenceDisplay, showPersistentPrompts: resolvedShowPersistentPrompts, disableConversationPersistence: disableConversationPersistence, 
                                        // Semantic props - NO generic flags
                                        hideFirstMessage: hideFirstMessage, disableInternalScroll: disableInternalScroll, disableAutoScroll: disableAutoScroll, renderCollapsed: renderCollapsed, noScroll: isDual && renderCollapsed && disableInternalScroll, messagesContainerRef: messagesContainerRef, streamingMessagesScrollRef: streamingMessagesScrollRef, conversationContainerRef: conversationContainerRef, disableArrowScroll: disableArrowScroll, disableAddSpaceAnimation: disableAddSpaceAnimation, topSpaceReserved: topSpaceReserved, layoutMode: layoutMode, collapsedMaxHeight: collapsedMaxHeightValue, onInputHeightChange: isDual ? setMeasuredInputHeight : undefined, messageStackMode: resolvedMessageStackMode, conversationType: layoutMode === 'dual' ? 'dualSearch' : 'aiModeSearch' })] }) }), isDual && (_jsx(Box, { sx: rightPanelStyle, children: selectedProduct && (_jsxs(Box, { sx: selectedProductWrapperStyle, ref: drawerWrapperRef, children: [_jsx(Box, { sx: {
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'flex-start',
                                            padding: '12px 16px',
                                            borderBottom: '1px solid #e5e7eb',
                                            flexShrink: 0,
                                            minHeight: 'calc(var(--header-height, 3.5rem) + 1px)',
                                        }, children: _jsx(IconButton, { onClick: handleCloseClick, "aria-label": "close", sx: {
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                },
                                                borderRadius: '8px',
                                                opacity: 0.8,
                                            }, children: _jsx(ChevronRight, {}) }) }), _jsx(Box, { sx: productPanelBodyStyle, children: _jsx(ProductDetailPanelWrapper, { product: selectedProduct }) })] })) })), !isDual && (_jsx("div", { style: {
                                display: 'flex',
                                flexDirection: 'column',
                                backgroundColor: '#f9fafb',
                                borderLeft: '1px solid #e5e7eb',
                                overflow: 'hidden',
                            }, children: selectedProduct && (_jsx(ProductDetailPanelWrapper, { product: selectedProduct })) }))] }), isDual && collapseAllowed && contentExceedsThreshold && (_jsx(Box, { ref: seeMoreButtonContainerRef, sx: {
                        // Always anchor to the bottom and float above the right panel
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        zIndex: 15,
                        padding: 0,
                        borderTop: '1px solid #e5e7eb',
                        backgroundColor: '#ffffff',
                        display: 'flex',
                        justifyContent: 'center',
                        pointerEvents: 'auto',
                    }, children: _jsx(Button, { ref: seeMoreButtonRef, className: "site-system-button-secondary", onClick: handleToggleCollapse, sx: {
                            width: '100%',
                            maxWidth: '400px',
                            margin: 0,
                            textTransform: 'none',
                        }, children: renderCollapsed
                            ? (resolvedTopSectionConfig?.seeMoreLabel || seeMoreText)
                            : (resolvedTopSectionConfig?.seeLessLabel || seeLessText) }) })), !aiSearchEnabled && (_jsx(Box, { sx: {
                        height: 56,
                        minHeight: 56,
                        width: '100%',
                    } }))] }) }));
};
// @ts-expect-error - defaultProps may have incompatible types from Conversation.defaultProps
// (e.g., itemReferenceDisplay as string vs 'card' | 'link')
Search.defaultProps = {
    searchQuery: undefined,
    layoutMode: 'ai',
    allowCollapseSearchWidget: false,
    // Conversation.defaultProps already includes hideFirstMessage and disableAutoScroll
    ...Conversation.defaultProps,
    // Override messageStackMode - it should be calculated based on layoutMode, not use default
    messageStackMode: undefined,
};
export default observer(Search);
//# sourceMappingURL=Search.js.map